import { compose } from 'ramda';
import React, { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import InPageAnnouncement from '_components/Announcements/InPageAnnouncement';
import { PageContainer } from '_components/MMFBPPAssessment/StyledBPPAssessment';
import { EditMode } from '_components/MMFContactDetailsForm/Sections';
import Icon from '_components/Icon';
import ApiErrorContent from './Sections/ApiErrorContent';
import Modal from '_components/MMFMembershipRenewalForm/Sections/Declaration/Modal';
import {
  withDataSourceValidation,
  withEditMode,
} from '_containers/BaseComponent';
import ContactDetailsFormContextProvider from '_containers/MMFContactDetailsFormContext';
import { withDigitalDataContext } from '_containers/DigitalDataContext';
import { useLocationAPIContext } from '_containers/LocationAPIContext';
import { withMemberContext } from '_containers/MemberContext';
import ModalProvider from '_containers/ModalContext';
import { DiscardChangesAlert } from '_utils/components/Form/DiscardChangesAlert';
import StickyFooter from '_utils/components/Form/StickyFooter';
import {
  Container,
  FormContainer,
  IntroContainer,
  NarrowContainer,
  SpinnerWrapper,
  StyledSpinner
} from '_utils/components/Form/StyledContainerForm';
import { getDictionaryItem } from '_utils/data/dictionaryItem';
import { emitTrackEvent, setObjectData } from '_utils/helpers/analytics';
import {
  checkUserRerferredFromRenewal
} from '_utils/helpers/form';

import { ContactDetailsFormProps } from './definitions';
import FormContent from './FormContent';
import { contactDetailsFormValidators } from './validation';

import {
  BackToRenewalFormButton,
  BackToRenewalFormButtonLabel,
  BackToRenewalFormContent,
  CTAButtons,
} from './StyledContactDetailsForm';
import { redirectUrls } from '_utils/helpers/urlFuncs';
 
const MMFContactDetailsForm: React.FC<ContactDetailsFormProps> = (props) => {
  const {
    contactProfileApi,
    contactProfileSubmissionApi,
    digitalData,
    editMode,
    rendering,
    setDigitalData,
  } = props;

  const { countriesApi } = useLocationAPIContext();
  const history = useHistory();

  const successHeading = getDictionaryItem('form-updates-saved', 'Updates saved');
  const errorHeading = getDictionaryItem('form-errors-heading', 'Something went wrong');
  const backToRenewalFormLabelText = getDictionaryItem('form-back-to-renewal-button-label', 'Back to membership renewal');

  const shouldShowStickyFooter = useRef<boolean>(false);

  const redirectValues = rendering?.fields?.data?.datasource?.submissionRedirectLinks?.values;
  const redirectValuesName = rendering?.fields?.data?.datasource?.submissionRedirectLinks?.values.map((a) => a.name.toLowerCase());

  // only check referrer to should show footer on initial mount, not qs change
  useEffect(() => {
    shouldShowStickyFooter.current = checkUserRerferredFromRenewal();
  }, []);

  const uid = rendering?.uid || null;

  const hasAnyAPIError = contactProfileApi?.result?.error || contactProfileSubmissionApi?.result?.error;
  const getAPIErrorMessage = () => {
    if (typeof contactProfileApi?.result?.error?.message === 'string') {
      return contactProfileApi?.result?.error?.message;
    }

    if (typeof contactProfileSubmissionApi?.result?.error?.message !== 'string') {
      return (
        contactProfileSubmissionApi?.result?.error?.message?.response?.data?.error
        || contactProfileSubmissionApi?.result?.error?.message?.response?.data?.errors?.[0]?.message
      );
    }
  };

  const shouldShowAPIError = hasAnyAPIError;
  const shouldShowAPISuccess = !hasAnyAPIError && contactProfileSubmissionApi?.result?.data;
  const shouldShowSpinner = !hasAnyAPIError && (
    contactProfileApi?.result?.isLoading
    || countriesApi?.result?.isLoading
    || contactProfileSubmissionApi?.result?.isLoading
  );

  if (shouldShowAPISuccess && successHeading.length > 1) {
    redirectUrls('?referral=', redirectValuesName, redirectValues)
  }

  const handleReturnButtonClick = () => {
    // TODO: might need the URL for Membership Renewal form from BE defined, so that this won't fail?
    history.goBack();
  };

  // render component with mock data when in Experience Editor
  if (editMode) {
    return (
      <EditMode {...props} />
    )
  }

  // dispatch tracking event when API error occur and form loaded
  useEffect(() => {
    if (
      !editMode &&
      !contactProfileApi?.result?.isLoading &&
      hasAnyAPIError &&
      typeof setDigitalData === 'function'
    ) {
      setDigitalData(
        setObjectData(
          ['form'],
          {
            formName: 'contact Details form',
            errorName: getAPIErrorMessage()
          },
          digitalData)
      );
      emitTrackEvent('formErrors');
    }
  }, [hasAnyAPIError, contactProfileApi?.result?.isLoading]);

  return (
    <ContactDetailsFormContextProvider>
      <DiscardChangesAlert />

      <Container id={uid} className="contact-details-form">
        <ModalProvider>
          <FormContainer>
            <PageContainer>
              {shouldShowAPIError &&
                <>
                  <IntroContainer>
                    <InPageAnnouncement
                      description={{ value: getAPIErrorMessage() }}
                      title={{ value: errorHeading }}
                      type={{ value: 'Red' }}
                    />
                  </IntroContainer>

                  {contactProfileApi?.result?.error?.message &&
                    <NarrowContainer>
                      <ApiErrorContent {...props} />
                    </NarrowContainer>
                  }
                </>
              }

              {shouldShowAPISuccess &&
                <IntroContainer>
                  <InPageAnnouncement
                    customIcon={{ targetItem: { name: 'check_circle' } }}
                    title={{ value: successHeading }}
                    type={{ value: 'Green' }}
                  />
                </IntroContainer>
              }

              {shouldShowSpinner &&
                <SpinnerWrapper>
                  <StyledSpinner showLoadingLabel={true} />
                </SpinnerWrapper>
              }

              <FormContent
                {...props}
              />
            </PageContainer >
          </FormContainer>

          <Modal />

          {shouldShowStickyFooter?.current && (
            <StickyFooter>
              <BackToRenewalFormContent config={{ col: { xs: 12 }, gutters: [] }}>
                <CTAButtons>
                  <BackToRenewalFormButton onClick={handleReturnButtonClick}>
                    <BackToRenewalFormButtonLabel>
                      <Icon name="arrow_back" />
                      {backToRenewalFormLabelText}
                    </BackToRenewalFormButtonLabel>
                  </BackToRenewalFormButton>
                </CTAButtons>
              </BackToRenewalFormContent>
            </StickyFooter>
          )}
        </ModalProvider>
      </Container>
    </ContactDetailsFormContextProvider>
  );
};

export default compose(
  withDataSourceValidation(contactDetailsFormValidators),
  withDigitalDataContext,
  withEditMode,
  withMemberContext,
)(MMFContactDetailsForm);
